import React, { useEffect, useRef, useState } from "react";
import BlockRenderer from "../BlockRenderer";
import cssVar from "./cssVar";
import { BLOCK } from "../../agent/agentContract";
import "../../copilot.scss";

/**
 * Dev-only gallery: one fixture per visual form, rendered through the REAL
 * BlockRenderer so what you see here is what the chat produces.
 *
 * Iterating on layout here costs nothing; iterating through the model costs an
 * inference round trip per tweak. Route: /copilot/fixtures (dev builds only —
 * see DashboardRouting).
 *
 * The wrapper carries .copilotPage because the runtime tokens are scoped to
 * that root; without it every var() falls back and the page renders unstyled.
 */
const FIXTURES = [
  {
    name: "stat_tile — count, positive delta, sparkline",
    block: {
      id: "fx1",
      type: BLOCK.STAT_TILE,
      label: "Emails sent",
      value: 12480,
      delta: { value: 12.4, unit: "%", direction: "up", sentiment: "positive", label: "vs prev. 30d" },
      sparkline: [30, 42, 38, 55, 61, 58, 74]
    }
  },
  {
    name: "stat_tile — rate, inverse metric (up but bad)",
    block: {
      id: "fx2",
      type: BLOCK.STAT_TILE,
      label: "Bounce rate",
      value: 4.28,
      unit: "%",
      delta: { value: 1.6, unit: "%", direction: "up", sentiment: "negative", label: "vs prev. 30d" },
      footnote: "Above the 3% threshold for two weeks."
    }
  },
  {
    name: "stat_tile — no delta, no sparkline",
    block: { id: "fx3", type: BLOCK.STAT_TILE, label: "Credits remaining", value: 412 }
  },
  {
    name: "stat_tile — flat delta, large number formatting",
    block: {
      id: "fx4",
      type: BLOCK.STAT_TILE,
      label: "Contacts reached",
      value: 1048576,
      delta: { value: 0, direction: "flat", sentiment: "neutral", label: "no change" }
    }
  },
  {
    name: "stat_tile — missing value renders an em dash, not NaN",
    block: { id: "fx5", type: BLOCK.STAT_TILE, label: "Meetings booked", value: null }
  },
  {
    name: "kpi_row — 4 tiles, mixed sentiment",
    block: {
      id: "fx6", type: BLOCK.KPI_ROW, title: "Sequence performance",
      tiles: [
        { label: "Sent", value: 12480 },
        { label: "Open rate", value: 41.3, unit: "%", delta: { value: 2.1, unit: "%", direction: "up", sentiment: "positive" } },
        { label: "Reply rate", value: 3.8, unit: "%", delta: { value: 1.3, unit: "%", direction: "down", sentiment: "negative" } },
        { label: "Bounce rate", value: 4.28, unit: "%", delta: { value: 1.6, unit: "%", direction: "up", sentiment: "negative" } }
      ]
    }
  },
  {
    name: "meter — tool-supplied threshold, healthy",
    block: {
      id: "fx7", type: BLOCK.METER, title: "Deliverability", kind: "deliverability",
      value: 97.2, unit: "%", threshold: { value: 95, unit: "%", label: "healthy above 95%" }
    }
  },
  {
    name: "meter — fallback threshold + inverse metric (under is good), failing",
    block: {
      id: "fx8", type: BLOCK.METER, title: "Bounce rate", kind: "bounce_rate", value: 4.28, unit: "%"
    }
  },
  {
    name: "meter — no threshold anywhere: refuses to render (nothing below)",
    block: { id: "fx9", type: BLOCK.METER, title: "Unknown ratio", kind: "not_configured", value: 42 }
  },
  {
    name: "funnel — step-to-step primary, cumulative secondary, worst step flagged",
    block: {
      id: "fx10", type: BLOCK.FUNNEL, title: "Sequence drop-off",
      stages: [
        { label: "Enrolled", value: 4000 },
        { label: "Step 1 sent", value: 3820 },
        { label: "Step 2 sent", value: 3610 },
        { label: "Step 3 sent", value: 1240 },
        { label: "Replied", value: 152 }
      ]
    }
  },
  {
    name: "diverging_bar — change vs previous period, grey midpoint",
    block: {
      id: "fx11", type: BLOCK.DIVERGING_BAR, title: "Change vs previous 30 days", unit: "%",
      rows: [
        { label: "Opens", value: 12.4 }, { label: "Replies", value: -8.1 },
        { label: "Meetings", value: 22 }, { label: "Bounces", value: -3.2 },
        { label: "Unsubs", value: 0 }
      ]
    }
  },
  {
    name: "dumbbell — A/B variants, before vs after",
    block: {
      id: "fx12", type: BLOCK.DUMBBELL, title: "Variant A vs B", unit: "%",
      fromLabel: "Variant A", toLabel: "Variant B",
      rows: [
        { label: "Open rate", from: 38.2, to: 44.9 },
        { label: "Reply rate", from: 3.1, to: 4.6 },
        { label: "Meeting rate", from: 0.8, to: 0.7 }
      ]
    }
  },
  {
    name: "heatmap — send times, day x hour, sequential ramp",
    block: {
      id: "fx13", type: BLOCK.HEATMAP, title: "Reply rate by send time", unit: "%",
      rows: ["Mon","Tue","Wed","Thu","Fri"].map((day, di) => ({
        label: day,
        values: ["8a","10a","12p","2p","4p","6p"].map((hour, hi) => ({
          label: hour, value: Math.round(((di * 7 + hi * 11) % 17) * 1.4 * 10) / 10
        }))
      }))
    }
  },
  {
    name: "comparison_grid — 3 options, one recommended",
    block: {
      id: "fx14", type: BLOCK.COMPARISON_GRID, title: "Which sequence should I clone?",
      options: [
        { title: "Outbound Q3", subtitle: "6 steps", value: 4.8, unit: "%", points: ["Highest reply rate", "Ran 3 months"], prompt: "Clone Outbound Q3" },
        { title: "Founder-led", subtitle: "4 steps", value: 6.2, unit: "%", recommended: true, points: ["Best meeting rate", "Shortest cycle"], prompt: "Clone Founder-led", ctaLabel: "Clone this" },
        { title: "Nurture", subtitle: "9 steps", value: 1.9, unit: "%", points: ["Long horizon"], prompt: "Clone Nurture" }
      ]
    }
  },
  {
    name: "data_table — inline rows, sortable, single page",
    block: {
      id: "fx15", type: BLOCK.DATA_TABLE, title: "Team performance",
      columns: [
        { key: "name", label: "Member" },
        { key: "sent", label: "Sent", numeric: true },
        { key: "replyRate", label: "Reply rate", numeric: true, unit: "%" }
      ],
      rows: [
        { id: 1, name: "Ada Lovelace", sent: 1204, replyRate: 5.2 },
        { id: 2, name: "Grace Hopper", sent: 980, replyRate: 6.1 },
        { id: 3, name: "Alan Turing", sent: 1450, replyRate: 3.4 },
        { id: 4, name: "Katherine Johnson", sent: 875, replyRate: null }
      ]
    }
  },
  {
    name: "stepper — cycle, Next wraps last to first",
    block: {
      id: "fx16", type: BLOCK.STEPPER, title: "How inbox warmup works",
      stages: [
        { title: "1. Seed sends", body: "A small number of warmup emails go out to the peer pool.", meta: "Day 1-3" },
        { title: "2. Peer engagement", body: "Recipients open, reply, and move messages out of spam.", meta: "Day 3-10" },
        { title: "3. Reputation rises", body: "Providers observe positive signals and raise your sender score.", meta: "Day 10-21" },
        { title: "4. Volume increases", body: "Warmup volume steps up, feeding step 1 again.", meta: "Continuous" }
      ]
    }
  },
  {
    name: "diagram — flowchart with two clickable groups (click sends a prompt)",
    block: {
      id: "fx17", type: BLOCK.DIAGRAM, title: "How this sequence runs",
      svg: `<svg viewBox="0 0 560 120" xmlns="http://www.w3.org/2000/svg">
        <defs><marker id="ar" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 z" fill="currentColor"/></marker></defs>
        <g data-prompt="Tell me about step 1 of this sequence">
          <rect x="8" y="35" width="120" height="48" rx="8" fill="none" stroke="currentColor"/>
          <text x="68" y="64" text-anchor="middle" font-size="12" fill="currentColor">Email 1</text>
        </g>
        <line x1="132" y1="59" x2="188" y2="59" stroke="currentColor" marker-end="url(#ar)"/>
        <g data-prompt="Why is there a 3 day wait here?">
          <rect x="192" y="35" width="120" height="48" rx="8" fill="none" stroke="currentColor"/>
          <text x="252" y="64" text-anchor="middle" font-size="12" fill="currentColor">Wait 3 days</text>
        </g>
        <line x1="316" y1="59" x2="372" y2="59" stroke="currentColor" marker-end="url(#ar)"/>
        <g><rect x="376" y="35" width="120" height="48" rx="8" fill="none" stroke="currentColor"/>
          <text x="436" y="64" text-anchor="middle" font-size="12" fill="currentColor">LinkedIn DM</text></g>
      </svg>`
    }
  },
  {
    name: "diagram — still streaming: skeleton, never half-drawn markup",
    streaming: true,
    block: { id: "fx18", type: BLOCK.DIAGRAM, title: "Drawing…", svg: '<svg viewBox="0 0 100 40"><rect x="5" y="5" wid' }
  },
  {
    name: "diagram — hostile input is rejected outright, not partially drawn",
    block: {
      id: "fx19", type: BLOCK.DIAGRAM, title: "Rejected diagram",
      svg: '<svg viewBox="0 0 10 10"><rect'
    }
  },
  {
    name: "diagram — script and handlers stripped, shape survives",
    block: {
      id: "fx20", type: BLOCK.DIAGRAM, title: "Sanitized diagram",
      svg: '<svg viewBox="0 0 200 60"><script>alert(1)</script><rect x="10" y="10" width="80" height="40" rx="6" onclick="alert(2)" fill="none" stroke="currentColor"/><text x="50" y="35" text-anchor="middle" font-size="12" fill="currentColor">Safe</text></svg>'
    }
  },
  {
    name: "widget — SANDBOX ESCAPE TEST: all three vectors must be BLOCKED (this is the real check; jsdom cannot prove it)",
    block: {
      id: "fx21", type: BLOCK.WIDGET, title: "Sandbox escape attempt",
      html: `<div id="out" style="font-family:monospace;font-size:12px"></div>
<script>
  var results = [];
  function probe(name, fn) {
    try {
      var value = fn();
      var leaked = value !== undefined && value !== null && value !== "";
      results.push((leaked ? "LEAKED  " : "blocked ") + name + " -> " + String(value).slice(0, 60));
    } catch (e) {
      results.push("blocked " + name + " -> threw " + e.name);
    }
  }
  probe("document.cookie", function () { return document.cookie; });
  probe("parent.document", function () { return parent.document.body.innerHTML.slice(0, 20); });
  probe("localStorage", function () { return localStorage.getItem("sg_copilot_api_key"); });
  probe("parent.location.href", function () { return parent.location.href; });
  probe("top.copilotStore", function () { return typeof top.copilotStore; });
  var ok = results.every(function (r) { return r.indexOf("blocked") === 0; });
  document.getElementById("out").innerHTML =
    '<div style="padding:8px;border-radius:6px;background:' + (ok ? '#e8f8ef' : '#fdecec') +
    ';color:' + (ok ? '#1a7f4b' : '#c0392b') + ';font-weight:600;margin-bottom:8px">' +
    (ok ? 'PASS - all escape vectors blocked' : 'FAIL - something leaked') + '</div>' +
    results.map(function (r) { return '<div>' + r + '</div>'; }).join("");
<\/script>`
    }
  },
  {
    name: "widget — interactive explainer: slider recomputes locally, button asks the chat",
    block: {
      id: "fx22", type: BLOCK.WIDGET, title: "What if reply rate improved?",
      html: `<label for="r">Reply rate: <b id="v">4</b>%</label>
<input id="r" type="range" min="1" max="15" value="4" step="0.5" />
<p style="color:var(--cp-ink-2)">On 1,000 sends that is <b id="c">40</b> replies.</p>
<button id="ask">Ask how to get there</button>
<script>
  var r = document.getElementById("r");
  function render() {
    document.getElementById("v").textContent = r.value;
    document.getElementById("c").textContent = Math.round(1000 * (r.value / 100));
  }
  r.addEventListener("input", render);
  document.getElementById("ask").addEventListener("click", function () {
    window.host.sendPrompt("How do I get my reply rate to " + r.value + "%?");
  });
  render();
<\/script>`
    }
  },
  {
    name: "bar_chart — emphasis: one bar carries the point, rest are grey context",
    block: {
      id: "fx23", type: BLOCK.BAR_CHART, title: "Revenue by acquisition channel", unit: "currency",
      rows: [
        { label: "Paid search", value: 512000 }, { label: "Organic", value: 398000 },
        { label: "Email", value: 287000 }, { label: "Referral", value: 190000 },
        { label: "Social", value: 118000 }, { label: "Direct", value: 46000 }
      ]
    }
  },
  {
    name: "bar_chart — explicit highlight on a mid-table row (worst step)",
    block: {
      id: "fx24", type: BLOCK.BAR_CHART, title: "Drop-off by step", unit: "%",
      highlight: "Step 3", preserveOrder: true,
      rows: [
        { label: "Step 1", value: 4.5 }, { label: "Step 2", value: 5.5 },
        { label: "Step 3", value: 65.6 }, { label: "Step 4", value: 12.2 }
      ]
    }
  },
  {
    name: "line_chart — two series, dash + marker shape as a second cue, named in legend",
    block: {
      id: "fx25", type: BLOCK.LINE_CHART, title: "Growth",
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      series: [
        { name: "Active users", data: [11200, 12800, 13500, 15100, 16800, 18420] },
        { name: "Sessions", data: [24000, 26400, 27800, 31100, 35400, 39600] }
      ]
    }
  },
  {
    name: "line_chart — single series: no legend (the title already names it)",
    block: {
      id: "fx26", type: BLOCK.LINE_CHART, title: "Reply rate over time", unit: "%",
      categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      series: [{ name: "Reply rate", data: [3.1, 3.4, 3.2, 4.0, 4.4, 4.8] }]
    }
  }
];

const VisualFixtures = () => {
  const rootRef = useRef(null);
  const [tokensMissing, setTokensMissing] = useState(false);

  // The runtime tokens are scoped to .copilotPage. Anything mounted outside
  // that selector loses every colour SILENTLY — vars just resolve to nothing.
  // Assert it here rather than discovering it after N components look grey.
  useEffect(() => {
    const probe = cssVar("--cp-series-1", rootRef.current);
    setTokensMissing(!probe);
  }, []);

  return (
  <div className="copilotPage cpFixtures" ref={rootRef}>
    {tokensMissing && (
      <div className="cpFixtures__alarm">
        Runtime tokens are NOT resolving. This tree is mounted outside
        .copilotPage — every visual below is rendering without its palette.
      </div>
    )}
    <h1 className="cpFixtures__title">Copilot visual fixtures</h1>
    <p className="cpFixtures__note">
      Rendered through BlockRenderer with the real runtime tokens. Narrow the
      window to check behaviour at panel width.
    </p>
    {FIXTURES.map(fixture => (
      <section className="cpFixtures__case" key={fixture.block.id}>
        <h2 className="cpFixtures__caseName">{fixture.name}</h2>
        <BlockRenderer block={fixture.block} isStreaming={fixture.streaming === true} />
      </section>
    ))}
  </div>
  );
};

export default VisualFixtures;
